import { movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import type { Media } from "@/types/media";

export const allMedia: Media[] = movies as Media[];

export function getMediaById(id: string | undefined): Media | undefined {
  if (!id) return undefined;
  return allMedia.find((item) => item.id === id);
}

export function getCategory(slug: string | undefined) {
  if (!slug) return undefined;
  return categories.find((item) => item.slug === slug);
}

export function getCategoryName(slug: string): string {
  return getCategory(slug)?.name ?? "全部影视";
}

export function getMediaByType(slug: string | undefined): Media[] {
  if (!slug) return allMedia;
  return allMedia.filter((item) => item.type === slug);
}

export function getRegions(list: Media[]): string[] {
  return Array.from(new Set(list.map((item) => item.region)));
}

export function getYears(list: Media[]): number[] {
  return Array.from(new Set(list.map((item) => item.year))).sort((a, b) => b - a);
}

export interface FilterOptions {
  region?: string;
  year?: string;
  rating?: string;
  keyword?: string;
}

export function filterMedia(list: Media[], options: FilterOptions): Media[] {
  const { region, year, rating, keyword } = options;
  return list.filter((item) => {
    if (region && region !== "全部" && item.region !== region) return false;
    if (year && year !== "全部" && String(item.year) !== year) return false;
    if (rating && rating !== "全部") {
      if (rating === "9" && item.rating < 9) return false;
      if (rating === "8" && (item.rating < 8 || item.rating >= 9)) return false;
      if (rating === "7" && (item.rating < 7 || item.rating >= 8)) return false;
    }
    if (keyword) {
      const kw = keyword.trim().toLowerCase();
      const haystack = [item.title, item.summary, item.region, ...item.genres]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(kw)) return false;
    }
    return true;
  });
}

export type SortKey = "hot" | "new" | "rating";

export function sortMedia(list: Media[], key: SortKey): Media[] {
  const cloned = [...list];
  if (key === "hot") cloned.sort((a, b) => b.views - a.views);
  if (key === "new") cloned.sort((a, b) => b.addedAt.localeCompare(a.addedAt));
  if (key === "rating") cloned.sort((a, b) => b.rating - a.rating);
  return cloned;
}

export function getHotMedia(limit = 8): Media[] {
  return sortMedia(allMedia, "hot").slice(0, limit);
}

export function getNewMedia(limit = 10): Media[] {
  return sortMedia(allMedia, "new").slice(0, limit);
}

export function getTopRated(limit = 6): Media[] {
  return sortMedia(allMedia, "rating").slice(0, limit);
}

export function getRelated(media: Media, limit = 6): Media[] {
  const sameType = allMedia.filter((item) => item.id !== media.id && item.type === media.type);
  const sameGenre = allMedia.filter(
    (item) =>
      item.id !== media.id &&
      item.type !== media.type &&
      item.genres.some((g) => media.genres.includes(g)),
  );
  return [...sameType, ...sameGenre].slice(0, limit);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万次播放`;
  return `${views} 次播放`;
}